import { ProduktspecifikationDTO } from "~app/common/models/produkt/ProduktspecifikationDTO";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ValidationContext } from "~app/common/validation/validationContext";
import { ProduktDetailTexts } from "../../produkt-detail.texts";

export class ProduktDetailBasValidation {
    static isNumberBetween0and1000AndOnlyOneDecimal(newValue: number): boolean {
        // eslint-disable-next-line sonarjs/concise-regex
        return !/^[0-9]+(\.[0-9]{1})?$/.test(newValue.toString()) || newValue <= 0 || newValue > 1000;
    }

    static init(produktspecifikation: ProduktspecifikationDTO, matningsinstruktionlank: string, nyLangdmodul: number): ValidationContext {
        const context = new ValidationContext();
        context.errorLevel = ValidationErrorLevels.error;
        context.create(ValidationScopes.spara).withRules([
            {
                errorLevel: ValidationErrorLevels.error,
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) ||
                // eslint-disable-next-line sonarjs/slow-regex, sonarjs/empty-string-repetition
                    newValue.length === 0 || /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(newValue),
                message: { text: ProduktDetailTexts.basTab.validation.matningsinstruktionslank },
                model: () => matningsinstruktionlank,
                name: "matningsinstruktionlank"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) ||
                    !(ProduktDetailBasValidation.isNumberBetween0and1000AndOnlyOneDecimal(newValue) ||
                    (newValue > produktspecifikation.diaToppMax && !ObjectUtils.isNullOrUndefined(produktspecifikation.diaToppMax))),
                message: { text: ProduktDetailTexts.basTab.validation.diaTopMin },
                model: () => produktspecifikation.diaToppMin,
                name: "diaToppMin",
                subscriptions: ["diaToppMax"],
                publish: true,
                verbose: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) ||
                    !(ProduktDetailBasValidation.isNumberBetween0and1000AndOnlyOneDecimal(newValue) ||
                    (newValue < produktspecifikation.diaToppMin && !ObjectUtils.isNullOrUndefined(produktspecifikation.diaToppMin))),
                message: { text: ProduktDetailTexts.basTab.validation.diaTopMax },
                model: () => produktspecifikation.diaToppMax,
                name: "diaToppMax",
                subscriptions: ["diaToppMin"],
                publish: true,
                verbose: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) ||
                // eslint-disable-next-line sonarjs/concise-regex
                    !(!/^[0-9]+(\.[0-9]{1})?$/.test(newValue as string) || newValue <= 0 || newValue > 1000),
                message: { text: ProduktDetailTexts.basTab.validation.diaRotMax },
                model: () => produktspecifikation.diaRotMax,
                name: "diaRotMax"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) ||
                    !(!/^\d+$/.test(newValue as string) ||
                    newValue <= 0 ||
                    newValue > 5000 ||
                    (newValue > produktspecifikation.langdgransMax && !ObjectUtils.isNullOrUndefined(produktspecifikation.langdgransMax))),
                message: { text: ProduktDetailTexts.basTab.validation.langdgransMin },
                model: () => produktspecifikation.langdgransMin,
                name: "langdgransMin",
                subscriptions: ["langdgransMax"],
                publish: true,
                verbose: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) ||
                    !(!/^\d+$/.test(newValue as string) ||
                    newValue <= 0 ||
                    newValue > 5000 ||
                    (newValue < produktspecifikation.langdgransMin && !ObjectUtils.isNullOrUndefined(produktspecifikation.langdgransMin))),
                message: { text: ProduktDetailTexts.basTab.validation.langdgransMax },
                model: () => produktspecifikation.langdgransMax,
                name: "langdgransMax",
                subscriptions: ["langdgransMin"],
                publish: true,
                verbose: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) ||
                // eslint-disable-next-line sonarjs/concise-regex
                    !(!/^[0-9]+(\.[0-9]{1})?$/.test(newValue as string) ||
                    newValue <= 0 ||
                    newValue > 50 ||
                    (newValue > produktspecifikation.overmalMax && !ObjectUtils.isNullOrUndefined(produktspecifikation.overmalMax))),
                message: { text: ProduktDetailTexts.basTab.validation.overmalMin },
                model: () => produktspecifikation.overmalMin,
                name: "overmalMin",
                subscriptions: ["overmalMax"],
                publish: true,
                verbose: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) ||
                // eslint-disable-next-line sonarjs/concise-regex
                    !(!/^[0-9]+(\.[0-9]{1})?$/.test(newValue as string) ||
                    newValue <= 0 ||
                    newValue > 50 ||
                    (newValue < produktspecifikation.overmalMin && !ObjectUtils.isNullOrUndefined(produktspecifikation.overmalMin))),
                message: { text: ProduktDetailTexts.basTab.validation.overmalMax },
                model: () => produktspecifikation.overmalMax,
                name: "overmalMax",
                subscriptions: ["overmalMin"],
                publish: true,
                verbose: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) || !(
                    newValue < produktspecifikation.langdgransMin ||
                    newValue > produktspecifikation.langdgransMax ||
                    produktspecifikation.produktLangdmoduler.some(p => p.modul === newValue)),
                message: { text: ProduktDetailTexts.basTab.validation.nyLangdmodul },
                model: () => nyLangdmodul,
                name: "nyLangdmodul"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) || !(!/^\d+$/.test(newValue as string) || newValue > 100 || newValue < 0),
                message: { text: ProduktDetailTexts.basTab.validation.fukthalt },
                model: () => produktspecifikation.fukthalt,
                name: "fukthalt"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                // eslint-disable-next-line sonarjs/concise-regex
                expression: (newValue) => ObjectUtils.isNullOrUndefined(newValue) || !(!/^[0-9]+(\.[0-9]{1})?$/.test(newValue as string) || newValue > 30 || newValue < 0.1),
                message: { text: ProduktDetailTexts.basTab.validation.flisstorlekOnskad },
                model: () => produktspecifikation.flisstorlekOnskad,
                name: "flisstorlekOnskad"
            }
        ]);

        return context;
    }
}
